import React from 'react';
import { Box, Grid } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const OrphanMarketplaceTab = ({ metrics, timeSeries }) => {
  // Merge marketplace data for the chart
  const marketplaceData = timeSeries.orphansListed.map((item, index) => ({
    date: item.date,
    listed: item.count,
    adopted: timeSeries.adoptions[index]?.count || 0,
    applications: timeSeries.applications[index]?.count || 0,
  }));

  return (
    <Box>
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        <Grid item xs={12} md={2.4}>
          <StatBox label="Active Listings" value={metrics.orphansListed.active} color="#8B5CF6" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatBox label="Total Listed" value={metrics.orphansListed.total} color="#3B82F6" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatBox label="Adoptions" value={metrics.adoptions.total} color="#10B981" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatBox label="Conversion" value={`${metrics.adoptions.conversionRate}%`} color="#F59E0B" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatBox label="Pending Apps" value={metrics.applications.pending} color="#EF4444" />
        </Grid>
      </Grid>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#8B5CF6' }}>
          Marketplace Activity (14 Days)
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={marketplaceData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="date"
              stroke="#64748B"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => {
                const d = new Date(value);
                return `${d.getMonth() + 1}/${d.getDate()}`;
              }}
            />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                background: '#1E293B',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#F1F5F9',
              }}
            />
            <Legend wrapperStyle={{ color: '#94A3B8' }} />
            <Line
              type="monotone"
              dataKey="listed"
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={false}
              name="Listed"
            />
            <Line
              type="monotone"
              dataKey="adopted"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
              name="Adopted"
            />
            <Line
              type="monotone"
              dataKey="applications"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={false}
              name="Applications"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#3B82F6' }}>
          Active Listings
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {metrics.activeOrphanListings.map((orphan) => (
            <Box
              key={orphan.id}
              component="a"
              href={`/orphan-marketplace/${orphan.id}`}
              target="_blank"
              sx={{
                background: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textDecoration: 'none',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#8B5CF6',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box>
                <Box sx={{ fontSize: '15px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                  {orphan.leagueName}
                </Box>
                <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                  {orphan.teamName && `${orphan.teamName} · `}
                  {orphan.buyIn && `$${orphan.buyIn} · `}
                  Listed {new Date(orphan.createdAt).toLocaleDateString()}
                </Box>
              </Box>
              <Box sx={{ fontSize: '24px' }}>🏆</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const StatBox = ({ label, value, color }) => (
  <Box
    sx={{
      background: '#1E293B',
      border: `2px solid ${color}`,
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}44`,
      },
    }}
  >
    <Box sx={{ fontSize: '48px', fontWeight: 700, color, marginBottom: '8px' }}>
      {value}
    </Box>
    <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>
      {label}
    </Box>
  </Box>
);

export default OrphanMarketplaceTab;
